import SanitizedHTMLWrapper from '@/components/SanitizedHTMLWrapper'
import { userContext } from '@/contexts/userContext'
import PreviewPage from '@/pages/FileLibPage/components/preview'
import { FileInfoType } from '@/pages/FileLibPage/constants'
import { generateUUID } from '@/utils'
import { Pencil2Icon } from '@radix-ui/react-icons'
import { Image, Tooltip } from 'antd'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import AmrPlayer from '../audio/AmrPlayer'
import { PauseSvg } from '../audio/PauseSvg'
import { AudioPlaySvg } from '../audio/PlaySvg'
import { RunningSvg } from '../audio/RunningSvg'
import { useChatStore } from './chatStore'
import MaterialInfoDisplay from './components/MaterialInfoDisplay'
import './styles.less'
import { ContentType } from './types'
import { formatFileName, getFileIconByFileName, getSpecificPluginResultMap } from './utils'

/**
 * 用户消息组件
 *
 * 这是聊天系统中用于显示用户发送消息的组件，支持文本、图片、文件等多种消息类型。
 * 包含消息编辑、文件预览、音频播放、联想云同步等功能。
 *
 * 功能特点：
 * - 支持多种消息类型：文本、图片、文件
 * - 支持HTML内容渲染
 * - 支持文件预览功能
 * - 支持音频文件播放
 * - 支持AMR格式音频播放
 * - 支持联想云同步状态显示
 * - 支持消息编辑功能
 * - 响应式布局设计
 *
 * 使用场景：
 * 1. 用户文本消息 - 显示用户发送的文本内容
 * 2. 图片消息 - 显示用户上传的图片
 * 3. 文件消息 - 显示用户上传的文件
 * 4. 音频消息 - 播放用户上传的音频文件
 * 5. 混合消息 - 同时包含文本和文件的消息
 * 6. 消息编辑 - 允许用户编辑已发送的消息
 * 7. 文件预览 - 预览用户上传的文档
 * 8. 云同步 - 显示文件同步到联想云的状态
 *
 * 使用示例：
 * ```tsx
 * // 文本消息
 * <MessageUser
 *   userName="张三"
 *   data={[{ type: 'text', text: '你好，请问有什么可以帮助您的？' }]}
 * />
 *
 * // 图片消息
 * <MessageUser
 *   userName="李四"
 *   data={[{
 *     type: 'image_url',
 *     image_url: { url: 'image.jpg', name: 'screenshot.png' }
 *   }]}
 * />
 *
 * // 文件消息
 * <MessageUser
 *   userName="王五"
 *   data={[{
 *     type: 'file_url',
 *     file_url: { url: 'document.pdf', name: 'report.pdf' }
 *   }]}
 * />
 *
 * // 混合消息（文本+图片）
 * <MessageUser
 *   userName="赵六"
 *   data={[
 *     { type: 'text', text: '请查看这个图片' },
 *     { type: 'image_url', image_url: { url: 'image.jpg', name: 'photo.png' } }
 *   ]}
 * />
 * ```
 *
 * @param userName - 用户显示名称
 * @param data - 消息内容数组，支持多种类型的内容
 */
export default function MessageUser({ userName, data }: { userName: string; data: ContentType[] }) {
  // 从聊天状态管理中获取工具ID和音频播放状态
  const { tool_ids, tool_enable, audioPlayingId, setAudioPlayingId } = useChatStore()

  // 插件状态映射，用于跟踪特定插件的启用状态
  const pluginStatusMap = useRef({
    includedAudioPlugin: false,
    includedLenovoCloudPlugin: false
  })

  // 为当前音频实例生成唯一ID，用于全局音频播放状态管理
  const audioId = useRef(generateUUID(16))

  /**
   * 数据变化时更新插件状态映射
   */
  useEffect(() => {
    pluginStatusMap.current = getSpecificPluginResultMap(tool_ids, tool_enable)
  }, [data])

  // 从用户上下文中获取用户信息
  let { user } = useContext(userContext)
  /**
   * 解析消息数据，提取各种类型的内容
   * 当前支持：一条文本 + 一张图片，data最大长度为2
   */
  const [msg, imageUrl, imageName, fileItems, originalUrl, materialInfo] = useMemo(() => {
    if (!data.length) {
      return ['', '', '', [], '', []]
    }
    let msg = ''
    let imageUrl = ''
    let imageName = ''
    let originalUrl = ''
    const fileItems: Array<{ url: string; name: string; fileId: any }> = []
    let materialInfo: Array<{ type: string; name: string; url: string }> = []
    for (const item of data) {
      if (item.type === 'text') {
        msg = item.text
      } else if (item.type === 'image_url' && !imageUrl) {
        imageUrl = item.image_url?.url || ''
        originalUrl = item.image_url?.originalUrl || ''
        imageName = item?.image_url?.name || ''
      } else if (item.type === 'file_url') {
        fileItems.push({ url: item.file_url?.url || '', name: item.file_url?.name || '', fileId: item.file_url?.file_id || '' })
      } else if (item.type === 'material') {
        materialInfo = item?.material_info || []
      }
    }
    return [msg, imageUrl, imageName, fileItems, originalUrl, materialInfo]
  }, [data])

  /**
   * 处理消息重发功能
   * @param send - 是否发送消息（true）还是编辑（false）
   */
  const handleResend = send => {
    // 统一整理文件列表，支持多文件编辑回显与重发，携带 fileId 用于恢复 conversation_file_id
    const files = fileItems.filter(file => file.url || file.name).map(file => ({ fileUrl: file.url, fileName: file.name, fileId: file.fileId || '' }))
    const myEvent = new CustomEvent('userResendMsgEvent', {
      detail: {
        send,
        message: msg,
        imageUrl,
        imageName,
        originalUrl,
        // 兼容旧逻辑：单文件字段继续保留
        fileUrl: files[0]?.fileUrl || '',
        fileName: files[0]?.fileName || '',
        // 新增多文件字段，供输入框完整回显与重发
        files,
        /* 万象模型上传信息 */
        materialInfo: materialInfo
      }
    })
    document.dispatchEvent(myEvent)
  }

  // 文件预览相关状态
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewFile, setPreviewFile] = useState<FileInfoType>()

  /**
   * 处理文件预览功能
   * @param title - 文件标题
   * @param url - 文件URL
   */
  const handlePreview = (title, url) => {
    console.log('handlePreview', title, url)
    const fileInfo = {
      file_name: title,
      file_path: url
    }
    setPreviewFile(fileInfo)
    setPreviewOpen(true)
  }

  /**
   * 监听全局音频播放状态变化
   * 当其他音频开始播放时，暂停本组件内所有正在播放的音频
   */
  useEffect(() => {
    // audioKey 格式为 `{audioId}-{idx}`，用 `audioId + '-'` 做前缀匹配，避免 UUID 裸前缀歧义
    if (audioPlayingId && !audioPlayingId.startsWith(audioId.current + '-')) {
      fileItems.forEach((_, idx) => {
        const el = document.getElementById(`msg-audio-${audioId.current}-${idx}`) as HTMLAudioElement
        el?.pause()
      })
    }
  }, [audioPlayingId, fileItems])

  return (
    <div className="flex w-full justify-end py-1">
      <div className="w-fit max-w-[90%]">
        {/* 用户信息头部：用户名和头像 */}
        {/* <div className="flex w-fit gap-[12px] justify-self-end"> */}
        <div className="flexRowEnd w-full gap-[12px]">
          {userName && <span className="chat-role-name">{userName}</span>}
          <img src={user.avatar || '/user.png'} alt="" className="h-[24px] w-[24px] rounded-[12px]" />
        </div>

        {/* 用户消息内容区域 */}
        <div className="mr-[30px] flex rounded-[12px] bg-[#E0E7FF] p-[16px] dark:bg-[#333A48]">
          <div className="chat-custom-content whitespace-break-spaces break-all rounded-[12px] text-sm text-[#0D1638] dark:text-[#CFD5E8]">
            {/* {msg} */}
            {/* 文本消息内容 - 使用 SanitizedHTMLWrapper 进行 XSS 过滤 */}
            {/* {!!msg && <div className="custom-html-msg" dangerouslySetInnerHTML={{ __html: msg }} />} */}
            {!!msg && <SanitizedHTMLWrapper className="custom-html-msg" content={msg} onClick={() => {}} />}

            {/* 图片消息展示 */}
            {!!imageUrl && (
              <div className={`${msg ? 'mt-[10px]' : ''} h-[90px] w-[100px]`}>
                <Image
                  width={100}
                  height={90}
                  style={{ borderRadius: '6px' }}
                  className="cursonCase"
                  src={imageUrl}
                  preview={{ src: originalUrl || imageUrl }}
                  alt=""
                />
              </div>
            )}

            {/* 文件消息展示：支持多文件，逐个渲染卡片 */}
            {fileItems.length > 0 && (
              <div className={`flex flex-col gap-[8px] ${msg ? 'mt-[10px]' : ''}`}>
                {fileItems.map((file, idx) => {
                  const fType = file.name ? file.name.split('.').pop()?.toLowerCase() : ''
                  const audioKey = `${audioId.current}-${idx}`
                  return (
                    <div key={idx} className="flexRowStart usr-file-file-box">
                      {/* 文件图标 */}
                      <img src={getFileIconByFileName(file.name)} alt="" className="mr-[12px] h-[40px] w-[40px]" />
                      <div className="relative">
                        {['wav', 'mp3', 'm4a', 'ogg', 'opus', 'amr', 'aac'].includes(fType) ? (
                          <Tooltip title={file.name}>
                            <div className="usr-uploaded-file-filename" style={{ width: 208 }}>
                              {formatFileName(file.name)}
                            </div>
                          </Tooltip>
                        ) : (
                          <Tooltip title={file.name}>
                            <div
                              className="usr-uploaded-file-filename cursor-pointer"
                              onClick={() => handlePreview(file.name, file.url)}
                            >
                              {formatFileName(file.name)}
                            </div>
                          </Tooltip>
                        )}

                        {/* 联想云同步状态显示 */}
                        {pluginStatusMap.current.includedLenovoCloudPlugin &&
                          ['pdf', 'docx', 'doc', 'ppt', 'pptx'].includes(fType) && (
                            <div className="flexRowStart file-uploaded-cloud-box">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  fill-rule="evenodd"
                                  clip-rule="evenodd"
                                  d="M4 7C4 5.067 5.567 3.5 7.5 3.5C9.13657 3.5 10.5119 4.62372 10.8943 6.14237C10.9454 6.34534 11.1175 6.49501 11.3256 6.51742C12.8291 6.67937 14 7.95328 14 9.5C14 10.696 13.3002 11.7295 12.2855 12.2116C12.036 12.33 11.9299 12.6283 12.0484 12.8777C12.1669 13.1272 12.4651 13.2333 12.7145 13.1148C14.0648 12.4734 15 11.0964 15 9.5C15 7.55385 13.6105 5.93282 11.7692 5.57398C11.1728 3.78788 9.48735 2.5 7.5 2.5C5.06977 2.5 3.08948 4.42646 3.00295 6.83557C1.8194 7.39646 1 8.602 1 10C1 11.352 1.76676 12.5242 2.88682 13.1068C3.1318 13.2342 3.4337 13.1389 3.56113 12.8939C3.68856 12.6489 3.59326 12.347 3.34828 12.2196C2.54609 11.8023 2 10.9645 2 10C2 8.91117 2.69636 7.98369 3.66956 7.64109C3.87689 7.5681 4.01224 7.36826 4.00309 7.14864C4.00104 7.09938 4 7.04983 4 7ZM7.85355 13.3536L10.8536 10.3536C11.0488 10.1583 11.0488 9.84171 10.8536 9.64645C10.6583 9.45118 10.3417 9.45118 10.1464 9.64645L7.5 12.2929L6.35355 11.1464C6.15829 10.9512 5.84171 10.9512 5.64645 11.1464C5.45118 11.3417 5.45118 11.6583 5.64645 11.8536L7.14645 13.3536C7.34171 13.5488 7.65829 13.5488 7.85355 13.3536Z"
                                  fill="#7587A5"
                                />
                              </svg>
                              <span>已同步至联想云空间</span>
                            </div>
                          )}
                      </div>

                      {/* 音频播放控制 */}
                      {['wav', 'mp3', 'm4a', 'ogg', 'opus', 'aac'].includes(fType) && (
                        <>
                          {audioPlayingId !== audioKey && (
                            <span
                              className="cursor-pointer"
                              onClick={() => {
                                const el = document.getElementById(
                                  `msg-audio-${audioId.current}-${idx}`
                                ) as HTMLAudioElement
                                el?.play()
                              }}
                            >
                              {AudioPlaySvg}
                            </span>
                          )}
                          {audioPlayingId === audioKey && (
                            <div className="group relative">
                              <div className="audio-running-div block group-hover:hidden">{RunningSvg}</div>
                              <div
                                className="hidden cursor-pointer group-hover:block"
                                onClick={() => {
                                  const el = document.getElementById(
                                    `msg-audio-${audioId.current}-${idx}`
                                  ) as HTMLAudioElement
                                  el?.pause()
                                }}
                              >
                                {PauseSvg}
                              </div>
                            </div>
                          )}
                          <audio
                            id={`msg-audio-${audioId.current}-${idx}`}
                            src={file.url}
                            style={{ display: 'none' }}
                            onPlay={() => setAudioPlayingId(audioKey)}
                            onPause={() => {
                              if (audioPlayingId === audioKey) setAudioPlayingId(null)
                            }}
                            onEnded={() => setAudioPlayingId(null)}
                          />
                        </>
                      )}

                      {/* AMR格式音频 */}
                      {['amr'].includes(fType) && <AmrPlayer audioUrl={file.url} />}
                    </div>
                  )
                })}
              </div>
            )}

            {/* materialInfo 万象模型上传信息展示 */}
            {materialInfo && materialInfo.length > 0 && (
              <MaterialInfoDisplay
                materialInfo={materialInfo}
                hasText={!!msg}
                audioPlayingId={audioPlayingId}
                setAudioPlayingId={setAudioPlayingId}
                type="user"
              />
            )}
          </div>
        </div>

        {/* 消息操作区域：编辑按钮 */}
        {(msg || imageUrl || fileItems.length > 0) && (
          // 文本/图片/文件任一存在都可进入编辑态，支持附件回显
          <div className="mr-[32px] mt-2 flex justify-between ">
            <span></span>
            <div className="flex cursor-pointer gap-2 self-end text-gray-400">
              <Pencil2Icon className="hover:text-gray-500" onClick={() => handleResend(false)} />
            </div>
          </div>
        )}
      </div>

      {/* 文件预览模态框 */}
      {previewOpen && <PreviewPage open={previewOpen} fileInfo={previewFile} setOpen={setPreviewOpen} />}
    </div>
  )
}
